import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function AttendanceBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-gray-400 italic">No data</span>;
  }
  const color = pct >= 80 ? 'bg-emerald-100 text-emerald-700'
    : pct >= 60 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

function AttendanceBar({ pct, thin = false }) {
  if (pct === null || pct === undefined) return null;
  const clamped = Math.min(100, Math.max(0, pct));
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400';
  const h = thin ? 'h-1' : 'h-1.5';
  return (
    <div className={`w-full bg-gray-100 rounded-full ${h} mt-1`}>
      <div className={`${h} rounded-full transition-all ${barColor}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    ongoing:       'bg-emerald-100 text-emerald-700',
    completed:     'bg-blue-100 text-blue-700',
    'not started': 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${cfg[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || '—'}
    </span>
  );
}

function PersonAvatar({ name, imagePath, size = 'md' }) {
  const [imgError, setImgError] = useState(false);
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const dim = { sm: 'w-6 h-6 text-[9px]', md: 'w-9 h-9 text-[11px]', lg: 'w-10 h-10 text-xs', xl: 'w-20 h-20 text-lg' }[size] || 'w-9 h-9';
  const gradient = size === 'xl' || size === 'lg' ? 'from-purple-100 to-indigo-100' : 'from-slate-100 to-blue-100';
  const textColor = size === 'xl' || size === 'lg' ? 'text-indigo-700' : 'text-blue-700';
  if (!imgError && imagePath) {
    return (
      <img src={getImageURL(imagePath)} alt={name} onError={() => setImgError(true)}
        className={`${dim} rounded-full shrink-0 object-cover border-2 border-white shadow ring-1 ring-gray-200`} />
    );
  }
  return (
    <div className={`${dim} rounded-full shrink-0 border-2 border-white shadow ring-1 ring-gray-200 bg-gradient-to-br ${gradient} flex items-center justify-center`}>
      <span className={`font-bold ${textColor} leading-none`}>{initials}</span>
    </div>
  );
}

// ─── Student detail modal ─────────────────────────────────────────────────────

function StudentDetailModal({ subject, onClose }) {
  const [students, setStudents] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    axiosInstance.get('/admin/dashboard/batch-subject-attendance/students', {
      params: { batchSubjectId: subject.batchSubjectId },
    })
      .then((res) => {
        const list = (res.data?.students || []).slice()
          .sort((a, b) => (b.attendancePct ?? -1) - (a.attendancePct ?? -1));
        setStudents(list);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [subject.batchSubjectId]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const totalClasses = students?.[0]?.totalClasses ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <StatusBadge status={subject.status} />
                {subject.subjectCode && <span className="text-[11px] text-gray-400 font-medium">{subject.subjectCode}</span>}
              </div>
              <h2 className="text-base font-bold text-gray-900">{subject.subjectName}</h2>
              {subject.cohortLabel && <p className="text-[11px] text-gray-500 mt-0.5">{subject.cohortLabel}</p>}
              {(subject.startDate || subject.endDate) && (
                <p className="text-[11px] text-gray-400">{fmtDate(subject.startDate)} → {fmtDate(subject.endDate)}</p>
              )}
            </div>
            <button type="button" onClick={onClose}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-3 mt-3 ml-auto flex-wrap justify-end">
            <div className="text-center"><p className="text-lg font-bold text-gray-900">{subject.enrolledCount}</p><p className="text-[10px] text-gray-400">enrolled</p></div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="text-center"><p className="text-lg font-bold text-gray-900">{totalClasses}</p><p className="text-[10px] text-gray-400">classes held</p></div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="text-center"><AttendanceBadge pct={subject.attendancePct} /><p className="text-[10px] text-gray-400 mt-0.5">avg attendance</p></div>
          </div>
          <p className="text-[10px] text-gray-400 mt-2 italic">Formula: classes present ÷ {totalClasses} × 100 per student</p>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-12 px-6 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
          <div className="col-span-4">Student</div>
          <div className="col-span-2 text-center">Scheduled</div>
          <div className="col-span-2 text-center">Present</div>
          <div className="col-span-1 text-center">Absent</div>
          <div className="col-span-3 text-center">Attendance %</div>
        </div>

        {/* Scrollable list */}
        <div className="overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
              <svg className="animate-spin w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading student attendance…
            </div>
          )}
          {error && <p className="px-6 py-4 text-sm text-red-500 text-center">{error}</p>}
          {!loading && !error && students?.length === 0 && (
            <p className="px-6 py-10 text-sm text-gray-400 text-center italic">No enrolled students found.</p>
          )}
          {!loading && !error && students?.map((st, idx) => (
            <div key={st.studentId} className="grid grid-cols-12 items-center px-6 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50/70 transition-colors">
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <span className="text-[11px] text-gray-300 font-semibold w-4 shrink-0 text-right">{idx + 1}</span>
                <PersonAvatar name={st.studentName} imagePath={st.imagePath} size="lg" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 leading-snug break-words">{st.studentName}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    <span className="font-medium text-gray-500">{st.studentId}</span>
                    {st.registrationNumber && <span className="ml-1.5">· {st.registrationNumber}</span>}
                  </p>
                  {st.telephone && (
                    <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 0 1 2-2h3.28a1 1 0 0 1 .948.684l1.498 4.493a1 1 0 0 1-.502 1.21l-2.257 1.13a11.042 11.042 0 0 0 5.516 5.516l1.13-2.257a1 1 0 0 1 1.21-.502l4.493 1.498A1 1 0 0 1 21 15.72V19a2 2 0 0 1-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {st.telephone}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-span-2 text-center"><span className="text-sm font-semibold text-gray-700">{totalClasses}</span></div>
              <div className="col-span-2 text-center"><span className="text-sm font-bold text-emerald-600">{st.classesPresent}</span></div>
              <div className="col-span-1 text-center"><span className="text-sm font-bold text-red-400">{st.classesAbsent}</span></div>
              <div className="col-span-3 px-2">
                <div className="flex justify-center mb-0.5"><AttendanceBadge pct={st.attendancePct} /></div>
                <AttendanceBar pct={st.attendancePct} thin />
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function HoursCell({ allocated, scheduled, balance }) {
  if (!allocated && !scheduled) return <p className="text-xs text-gray-400 text-center">—</p>;
  const isOver    = balance < 0;
  const isPending = balance > 0;
  return (
    <div className="space-y-1 text-center">
      <div className="flex justify-center gap-1.5 flex-wrap">
        <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded font-semibold">{allocated}h alloc</span>
        <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded font-semibold">{scheduled}h sched</span>
      </div>
      {balance !== 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${isOver ? 'bg-orange-50 text-orange-700 border border-orange-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
          {isOver ? `+${Math.abs(balance)}h over` : `${balance}h pending`}
        </span>
      )}
      {balance === 0 && allocated > 0 && (
        <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded font-semibold">on track</span>
      )}
    </div>
  );
}

// ─── Assignment detail modal ──────────────────────────────────────────────────

const STATUS_CFG = {
  'on-time submission': 'bg-emerald-100 text-emerald-700',
  'late submission':    'bg-amber-100 text-amber-700',
  're-submission':      'bg-orange-100 text-orange-700',
};

function AssignmentDetailModal({ data, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${data.assignmentType === 'topup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {data.assignmentType === 'topup' ? 'Top-Up' : 'HND'}
                </span>
                {data.subjectCode && <span className="text-[11px] text-gray-400 font-medium">{data.subjectCode}</span>}
              </div>
              <h2 className="text-base font-bold text-gray-900">{data.subjectName || 'Unknown Subject'}</h2>
              {data.batchLabel && <p className="text-xs text-gray-500 mt-0.5">{data.batchLabel}</p>}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-center">
                <p className="text-xl font-bold text-orange-600">{data.items.length}</p>
                <p className="text-[10px] text-gray-400">uncorrected</p>
              </div>
              <button type="button" onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-12 px-6 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
          <div className="col-span-5">Student</div>
          <div className="col-span-3 text-center">Status</div>
          <div className="col-span-2 text-center">Type</div>
          <div className="col-span-2 text-center">Submitted</div>
        </div>

        {/* Student rows */}
        <div className="overflow-y-auto flex-1">
          {data.items.map((item, idx) => (
            <div key={`${item.assignmentType}-${item.assignmentId}`}
              className="grid grid-cols-12 px-6 py-3 border-b border-gray-100 last:border-0 hover:bg-orange-50/30 items-center gap-x-2">
              <div className="col-span-5 min-w-0 flex items-center gap-2">
                <span className="text-[11px] text-gray-300 font-semibold w-4 shrink-0 text-right">{idx + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{item.studentName || '—'}</p>
                  <p className="text-[10px] text-gray-400">{item.studentId}</p>
                </div>
              </div>
              <div className="col-span-3 flex justify-center">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${STATUS_CFG[item.submissionStatus] || 'bg-gray-100 text-gray-500'}`}>
                  {item.submissionStatus || '—'}
                </span>
              </div>
              <div className="col-span-2 flex justify-center">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${item.assignmentType === 'topup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {item.assignmentType === 'topup' ? 'Top-Up' : 'HND'}
                </span>
              </div>
              <div className="col-span-2 text-center">
                <p className="text-xs text-gray-500">
                  {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800 mb-0.5">{d.subjectName}</p>
      <p className="text-orange-600 font-bold">{d.count} uncorrected</p>
    </div>
  );
}

// ─── Select styles ────────────────────────────────────────────────────────────

function StaffOption({ data }) {
  return (
    <div className="flex items-center gap-2.5 py-0.5">
      <PersonAvatar name={data.label} imagePath={data.imagePath} size="sm" />
      <span className="text-sm text-gray-800">{data.label}</span>
    </div>
  );
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#7c3aed' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(124,58,237,0.2)' : 'none',
    '&:hover': { borderColor: '#7c3aed' },
    borderRadius: '0.5rem', minHeight: '42px', fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 10 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#ede9fe' : state.isFocused ? '#f5f3ff' : 'white',
    color: state.isSelected ? '#5b21b6' : '#1f2937', cursor: 'pointer',
  }),
  singleValue: (base) => ({ ...base, color: '#1f2937', fontSize: '0.875rem' }),
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function StaffAnalysisReport() {
  const navigate = useNavigate();

  const [staffOptions, setStaffOptions]       = useState([]);
  const [selectedStaff, setSelectedStaff]     = useState(null);
  const [startDate, setStartDate]             = useState('');
  const [endDate, setEndDate]                 = useState('');
  const [loadingOptions, setLoadingOptions]   = useState(true);
  const [report, setReport]                   = useState(null);
  const [generating, setGenerating]           = useState(false);
  const [genError, setGenError]               = useState(null);
  const [selectedCourse, setSelectedCourse]       = useState(null);
  const [statusFilter, setStatusFilter]           = useState('ongoing');
  const [modalSubject, setModalSubject]           = useState(null);
  const [selectedSubjectDetail, setSelectedSubjectDetail] = useState(null);

  useEffect(() => {
    axiosInstance.get('/admin/dashboard/staff-analysis/staff-list')
      .then((res) => {
        setStaffOptions((res.data?.staff || []).map((s) => ({ value: s.staffId, label: s.name, imagePath: s.imagePath })));
      })
      .catch(() => {})
      .finally(() => setLoadingOptions(false));

    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    setStartDate(`${y}-${m}-01`);
    setEndDate(`${y}-${m}-${lastDay}`);
  }, []);

  const generateReport = useCallback(async () => {
    if (!startDate || !endDate || !selectedStaff) return;
    setGenerating(true);
    setGenError(null);
    setReport(null);
    setSelectedCourse(null);
    setStatusFilter('ongoing');
    try {
      const res = await axiosInstance.get('/admin/dashboard/staff-analysis', {
        params: { startDate, endDate, staffIds: selectedStaff.value },
      });
      setReport(res.data);
    } catch (err) {
      setGenError(err?.response?.data?.message || 'Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  }, [startDate, endDate, selectedStaff]);

  const openModal  = useCallback((subj) => setModalSubject(subj), []);
  const closeModal = useCallback(() => setModalSubject(null), []);

  const staffData = report?.staff?.[0] || null;

  const courses = useMemo(() => {
    if (!staffData) return [];
    const seen = new Map();
    staffData.subjects.forEach((s) => {
      if (s.courseName && !seen.has(s.courseName)) {
        seen.set(s.courseName, { courseName: s.courseName, courseShortName: s.courseShortName || s.courseName });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.courseName.localeCompare(b.courseName));
  }, [staffData]);

  const filteredSubjects = useMemo(() => {
    if (!staffData) return [];
    return staffData.subjects.filter((s) => {
      if (selectedCourse && s.courseName !== selectedCourse) return false;
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      return true;
    });
  }, [staffData, selectedCourse, statusFilter]);

  return (
    <DashboardLayout title="Staff Analysis Report">
      <div className="min-h-screen bg-gray-50 px-6 py-8">

        {/* Page header */}
        <div className="mb-6 border-b border-gray-200 pb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">Staff Analysis Report</h1>
                <p className="text-sm text-gray-500 mt-0.5">Subject delivery hours and attendance analysis per staff member.</p>
              </div>
            </div>
            <button type="button" onClick={() => navigate('/admin/report/monitor')}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-gray-50 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Monitor Report
            </button>
          </div>
        </div>

        {/* Filter form */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Report Parameters</p>
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-6">
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Staff Member</label>
              <Select
                isLoading={loadingOptions}
                options={staffOptions}
                value={selectedStaff}
                onChange={setSelectedStaff}
                placeholder="Select a staff member…"
                formatOptionLabel={(opt) => <StaffOption data={opt} />}
                styles={selectStyles}
                isClearable
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">End Date</label>
              <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div className="col-span-2">
              <button type="button" onClick={generateReport} disabled={generating || !startDate || !endDate || !selectedStaff}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                {generating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Generating…
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z" />
                    </svg>
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {genError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700 mb-5">{genError}</div>
        )}

        {/* Results */}
        {report && !staffData && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-16 text-center text-gray-400">
            <p className="text-sm">No data found for the selected parameters.</p>
          </div>
        )}

        {staffData && (
          <div className="space-y-4">

            {/* Staff profile */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-5">
                <PersonAvatar name={staffData.staffName} imagePath={staffData.imagePath} size="xl" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">{staffData.staffName}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{fmtDate(report.startDate)} → {fmtDate(report.endDate)}</p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full font-semibold">{staffData.ongoingCount} ongoing</span>
                    <span className="text-[11px] bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full font-semibold">{staffData.completedCount} completed</span>
                    <span className="text-[11px] bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-full font-semibold">{staffData.subjects.length} subjects</span>
                    <span className="text-[11px] bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-full font-semibold">{staffData.totalClassesHeld} classes in range</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-gray-400 mb-1">Avg Attendance (in range)</p>
                  <AttendanceBadge pct={staffData.overallAttendancePct} />
                  <div className="w-32 mt-1.5"><AttendanceBar pct={staffData.overallAttendancePct} /></div>
                </div>
              </div>
            </div>

            {/* Course tabs + status filter */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setSelectedCourse(null)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${selectedCourse === null ? 'bg-purple-600 border-purple-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700'}`}>
                  All courses
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${selectedCourse === null ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {staffData.subjects.length}
                  </span>
                </button>
                {courses.map((c) => {
                  const count = staffData.subjects.filter((s) => s.courseName === c.courseName).length;
                  return (
                    <button key={c.courseName} type="button" title={c.courseName} onClick={() => setSelectedCourse(c.courseName)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${selectedCourse === c.courseName ? 'bg-purple-600 border-purple-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700'}`}>
                      {c.courseShortName}
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${selectedCourse === c.courseName ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm shrink-0">
                {[{ key: 'all', label: 'All' }, { key: 'ongoing', label: 'Ongoing' }, { key: 'completed', label: 'Completed' }].map(({ key, label }) => (
                  <button key={key} type="button" onClick={() => setStatusFilter(key)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === key ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-12 px-5 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-200">
                <div className="col-span-3">Subject</div>
                <div className="col-span-2">Batch</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-1 text-center">Students</div>
                <div className="col-span-3 text-center">Hours (Allocated · Scheduled · Balance)</div>
                <div className="col-span-1 text-center">Attendance %</div>
                <div className="col-span-1 text-center">Detail</div>
              </div>

              {filteredSubjects.length === 0 && (
                <p className="px-6 py-10 text-sm text-gray-400 text-center italic">No subjects match the selected filters.</p>
              )}

              {filteredSubjects.map((subj) => (
                <div key={subj.batchSubjectId}
                  className={`grid grid-cols-12 px-5 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 items-center gap-x-2 ${subj.classesHeld === 0 ? 'opacity-60' : ''}`}>

                  {/* Subject */}
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{subj.subjectName}</p>
                    {subj.subjectCode && <p className="text-[11px] text-gray-400">{subj.subjectCode}</p>}
                    {subj.startDate && <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(subj.startDate)} → {fmtDate(subj.endDate)}</p>}
                  </div>

                  {/* Batch */}
                  <div className="col-span-2 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{subj.cohortLabel}</p>
                    <p className="text-[11px] text-gray-400 truncate">{subj.courseName}</p>
                  </div>

                  {/* Status */}
                  <div className="col-span-1 flex justify-center"><StatusBadge status={subj.status} /></div>

                  {/* Students */}
                  <div className="col-span-1 text-center">
                    <p className="text-sm font-medium text-gray-700">{subj.enrolledCount}</p>
                  </div>

                  {/* Hours */}
                  <div className="col-span-3">
                    <HoursCell
                      allocated={subj.allocatedHours}
                      scheduled={subj.scheduledHours}
                      balance={subj.balanceHours}
                    />
                  </div>

                  {/* Attendance % */}
                  <div className="col-span-1 px-1">
                    <div className="flex justify-center"><AttendanceBadge pct={subj.attendancePct} /></div>
                    {subj.attendancePct !== null && <AttendanceBar pct={subj.attendancePct} />}
                  </div>

                  {/* View student detail */}
                  <div className="col-span-1 flex justify-center">
                    <button type="button" onClick={() => openModal(subj)}
                      title="View student-wise attendance"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-teal-50 border border-teal-200 text-teal-700 hover:bg-teal-100 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                      </svg>
                      {subj.enrolledCount}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Not Corrected Assignments — subject-wise chart */}
        {staffData && (() => {
          const list = staffData.notCorrectedAssignments || [];

          // Group by subject code/name — merge all batches & types for same subject
          const subjectMap = new Map();
          list.forEach((item) => {
            const key = item.subjectCode || item.subjectName || 'Unknown';
            if (!subjectMap.has(key)) {
              subjectMap.set(key, {
                key,
                subjectName: item.subjectName || 'Unknown',
                subjectCode: item.subjectCode || '',
                label: item.subjectCode || item.subjectName || 'Unknown',
                count: 0,
                items: [],
              });
            }
            const entry = subjectMap.get(key);
            entry.count += 1;
            entry.items.push(item);
          });
          const chartData = Array.from(subjectMap.values()).sort((a, b) => b.count - a.count);

          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Section header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Uncorrected Assignments</p>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${list.length > 0 ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {list.length}
                </span>
                {list.length > 0 && (
                  <span className="ml-auto text-[10px] text-gray-400 italic">Click a bar to view student details</span>
                )}
              </div>

              {list.length === 0 && (
                <div className="flex items-center gap-2 px-5 py-5 text-emerald-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                  <p className="text-sm font-medium">All assignments are corrected.</p>
                </div>
              )}

              {list.length > 0 && (
                <div className="px-5 pt-5 pb-4">
                  {/* Bar chart */}
                  <ResponsiveContainer width="100%" height={210}>
                    <BarChart data={chartData} margin={{ top: 20, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: '#fff7ed' }} />
                      <Bar dataKey="count" fill="#f97316" radius={[6, 6, 0, 0]} maxBarSize={64}>
                        <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 700, fill: '#374151' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Subject list with View Detail buttons */}
                  <div className="mt-4 border border-gray-100 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                      <div className="col-span-5">Subject</div>
                      <div className="col-span-2 text-center">Count</div>
                      <div className="col-span-3 text-center">Types</div>
                      <div className="col-span-2 text-center">Action</div>
                    </div>
                    {chartData.map((entry) => {
                      const hndCount   = entry.items.filter((i) => i.assignmentType === 'hnd').length;
                      const topupCount = entry.items.filter((i) => i.assignmentType === 'topup').length;
                      return (
                        <div key={entry.key} className="grid grid-cols-12 px-4 py-2.5 border-b border-gray-100 last:border-0 hover:bg-orange-50/30 items-center">
                          <div className="col-span-5 min-w-0">
                            <p className="text-sm font-semibold text-gray-800">{entry.subjectName}</p>
                            {entry.subjectCode && <p className="text-[10px] text-gray-400">{entry.subjectCode}</p>}
                          </div>
                          <div className="col-span-2 text-center">
                            <span className="text-sm font-bold text-orange-600">{entry.count}</span>
                          </div>
                          <div className="col-span-3 flex justify-center gap-1.5 flex-wrap">
                            {hndCount > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-orange-100 text-orange-700">{hndCount} HND</span>
                            )}
                            {topupCount > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700">{topupCount} Top-Up</span>
                            )}
                          </div>
                          <div className="col-span-2 flex justify-center">
                            <button
                              type="button"
                              onClick={() => setSelectedSubjectDetail(entry)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100 transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7S1.274 16.057 2.458 12z" />
                              </svg>
                              Detail
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Lecturer Hours Analysis */}
        {staffData?.lecturerHours && (() => {
          const { schedules, totalClasses, totalHours } = staffData.lecturerHours;
          const fmtHours = (h) => {
            const hrs = Math.floor(h);
            const mins = Math.round((h - hrs) * 60);
            return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
          };
          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                <div className="w-7 h-7 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Lecturer Hours Analysis</p>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  {totalClasses} classes · {fmtHours(totalHours)}
                </span>
                <p className="ml-auto text-[11px] text-gray-400 italic">Deduplicated — same subject/time/room counted once</p>
              </div>

              {totalClasses === 0 && (
                <p className="px-5 py-6 text-sm text-gray-400 text-center italic">No classes found in the selected date range.</p>
              )}

              {totalClasses > 0 && (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2">Subject</div>
                    <div className="col-span-3">Batch(es)</div>
                    <div className="col-span-2">Classroom</div>
                    <div className="col-span-1 text-center">Start</div>
                    <div className="col-span-1 text-center">End</div>
                    <div className="col-span-1 text-center">Duration</div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {schedules.map((sch, idx) => (
                      <div key={idx} className="grid grid-cols-12 px-5 py-3 border-b border-gray-100 last:border-0 hover:bg-indigo-50/20 items-start gap-x-2">
                        {/* Date */}
                        <div className="col-span-2">
                          <p className="text-sm font-medium text-gray-800">
                            {sch.date ? new Date(sch.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {sch.date ? new Date(sch.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' }) : ''}
                          </p>
                        </div>
                        {/* Subject */}
                        <div className="col-span-2 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{sch.subjectName || '—'}</p>
                          {sch.subjectCode && <p className="text-[10px] text-gray-400">{sch.subjectCode}</p>}
                        </div>
                        {/* Batches */}
                        <div className="col-span-3 min-w-0 flex flex-col gap-0.5">
                          {sch.batches && sch.batches.length > 0 ? (
                            sch.batches.map((b, i) => (
                              <span key={i} className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 truncate max-w-full">
                                {b}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                        {/* Classroom */}
                        <div className="col-span-2 min-w-0">
                          <p className="text-xs font-medium text-gray-700 truncate">{sch.classroomName || '—'}</p>
                          {sch.classroomFloor && <p className="text-[10px] text-gray-400">{sch.classroomFloor}</p>}
                        </div>
                        {/* Start */}
                        <div className="col-span-1 text-center pt-0.5">
                          <p className="text-xs font-mono font-medium text-gray-700">{sch.startTime || '—'}</p>
                        </div>
                        {/* End */}
                        <div className="col-span-1 text-center pt-0.5">
                          <p className="text-xs font-mono font-medium text-gray-700">{sch.endTime || '—'}</p>
                        </div>
                        {/* Duration */}
                        <div className="col-span-1 text-center pt-0.5">
                          <span className="text-xs font-bold text-indigo-600">{fmtHours(sch.durationHours)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Event Hours Analysis */}
        {staffData?.otherEvents && (() => {
          const events = staffData.otherEvents;
          const totalOtherHours = staffData.totalOtherHours || 0;
          const fmtHours = (h) => {
            const hrs = Math.floor(h);
            const mins = Math.round((h - hrs) * 60);
            return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
          };
          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Event / Sub-Event Hours</p>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  {events.length} event{events.length === 1 ? '' : 's'} · {fmtHours(totalOtherHours)}
                </span>
              </div>

              {events.length === 0 && (
                <p className="px-5 py-6 text-sm text-gray-400 text-center italic">No events assigned to this staff member in the selected date range.</p>
              )}

              {events.length > 0 && (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-4">Title</div>
                    <div className="col-span-2">Event Type</div>
                    <div className="col-span-1 text-center">Start</div>
                    <div className="col-span-1 text-center">End</div>
                    <div className="col-span-2 text-center">Duration</div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {events.map((ev) => (
                      <div key={ev.eventId} className="grid grid-cols-12 px-5 py-3 border-b border-gray-100 last:border-0 hover:bg-amber-50/20 items-start gap-x-2">
                        {/* Date */}
                        <div className="col-span-2">
                          <p className="text-sm font-medium text-gray-800">
                            {ev.date ? new Date(ev.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {ev.date ? new Date(ev.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' }) : ''}
                          </p>
                        </div>
                        {/* Title */}
                        <div className="col-span-4 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{ev.title || '—'}</p>
                        </div>
                        {/* Event Type */}
                        <div className="col-span-2 min-w-0">
                          {ev.eventTypeName ? (
                            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 truncate max-w-full">
                              {ev.eventTypeName}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                        {/* Start */}
                        <div className="col-span-1 text-center pt-0.5">
                          <p className="text-xs font-mono font-medium text-gray-700">{ev.startTime ? String(ev.startTime).slice(0, 5) : '—'}</p>
                        </div>
                        {/* End */}
                        <div className="col-span-1 text-center pt-0.5">
                          <p className="text-xs font-mono font-medium text-gray-700">{ev.endTime ? String(ev.endTime).slice(0, 5) : '—'}</p>
                        </div>
                        {/* Duration */}
                        <div className="col-span-2 text-center pt-0.5">
                          <span className="text-xs font-bold text-amber-600">{fmtHours(ev.hours)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Not Marked Attendance */}
        {staffData?.notMarkedAttendance && (() => {
          const list = staffData.notMarkedAttendance;
          const fmt12h = (t) => {
            if (!t) return '—';
            const [h, m] = t.split(':').map(Number);
            const suffix = h >= 12 ? 'PM' : 'AM';
            const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
            return `${h12}:${String(m || 0).padStart(2, '0')} ${suffix}`;
          };
          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Not Marked Attendance</p>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${list.length > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {list.length}
                </span>
              </div>

              {list.length === 0 && (
                <div className="flex items-center gap-2 px-5 py-5 text-emerald-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                  <p className="text-sm font-medium">All classes have attendance marked.</p>
                </div>
              )}

              {list.length > 0 && (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-1">Day</div>
                    <div className="col-span-3">Subject</div>
                    <div className="col-span-2">Batch / Group</div>
                    <div className="col-span-2">Classroom</div>
                    <div className="col-span-2 text-center">Time</div>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {list.map((item, idx) => {
                      const rawDateObj = item.date ? new Date(item.date + 'T00:00:00') : null;
                      // Displayed date is shown one day ahead of the stored value (e.g. DB "8 Jun" -> shown "9 Jun")
                      const dateObj = rawDateObj ? new Date(rawDateObj.getTime() + 24 * 60 * 60 * 1000) : null;
                      return (
                        <div key={idx} className="grid grid-cols-12 px-5 py-3 border-b border-gray-100 last:border-0 hover:bg-red-50/30 items-center gap-x-2">
                          {/* Date */}
                          <div className="col-span-2">
                            <p className="text-sm font-medium text-gray-800">
                              {dateObj ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {dateObj ? dateObj.getFullYear() : ''}
                            </p>
                          </div>
                          {/* Day */}
                          <div className="col-span-1">
                            <p className="text-xs text-gray-500">
                              {dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'short' }) : '—'}
                            </p>
                          </div>
                          {/* Subject */}
                          <div className="col-span-3 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{item.subjectName || '—'}</p>
                            {item.subjectCode && <p className="text-[10px] text-gray-400">{item.subjectCode}</p>}
                            {item.courseName && <p className="text-[10px] text-gray-400 truncate">{item.courseShortName || item.courseName}</p>}
                          </div>
                          {/* Batch / Group */}
                          <div className="col-span-2 min-w-0">
                            {item.batchLabel ? (
                              <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 truncate max-w-full">
                                {item.batchLabel}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                          {/* Classroom */}
                          <div className="col-span-2 min-w-0">
                            <p className="text-xs text-gray-600 truncate">{item.classroomName || '—'}</p>
                          </div>
                          {/* Time */}
                          <div className="col-span-2 text-center">
                            <p className="text-xs font-mono text-gray-700">
                              {fmt12h(item.startTime)} – {fmt12h(item.endTime)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Day-off & Half-day Analysis */}
        {staffData?.dayOffSummary && (() => {
          const { offDays, halfDays, totalAbsentHours, specificLeaves, rosterDates } = staffData.dayOffSummary;
          const hasRoster = (offDays.count + halfDays.count) > 0;
          const fmtH = (h) => {
            const hrs = Math.floor(h);
            const mins = Math.round((h - hrs) * 60);
            return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
          };
          const TYPE_CFG = {
            'off day':           { label: 'Off Day',           bg: 'bg-red-50',    text: 'text-red-700',   badge: 'bg-red-100 text-red-700' },
            'morning half day':  { label: 'Morning Half Day',  bg: 'bg-amber-50',  text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
            'evening half day':  { label: 'Evening Half Day',  bg: 'bg-orange-50', text: 'text-orange-700',badge: 'bg-orange-100 text-orange-700' },
          };
          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Off Days &amp; Half Days</p>
                {hasRoster && (
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    {fmtH(totalAbsentHours)} total absent
                  </span>
                )}
              </div>

              {!hasRoster && specificLeaves.length === 0 && (
                <p className="px-5 py-5 text-sm text-gray-400 italic">No roster schedule or specific leaves found for this staff.</p>
              )}

              {/* Roster-based stats */}
              {hasRoster && (
                <div className="grid grid-cols-3 gap-px bg-gray-100 border-b border-gray-100">
                  {/* Off Days */}
                  <div className="bg-white px-5 py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <p className="text-xs font-semibold text-gray-600">Off Days</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{offDays.count}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{offDays.passed} passed · {fmtH(offDays.hours)}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">@ 8.5h each</p>
                  </div>

                  {/* Half Days */}
                  <div className="bg-white px-5 py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <p className="text-xs font-semibold text-gray-600">Half Days</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{halfDays.count}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{halfDays.passed} passed · {fmtH(halfDays.hours)}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">@ 4h each</p>
                  </div>

                  {/* Total absent hours */}
                  <div className="bg-white px-5 py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-gray-400" />
                      <p className="text-xs font-semibold text-gray-600">Total Absent Hours</p>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{fmtH(totalAbsentHours)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {fmtH(offDays.hours)} off + {fmtH(halfDays.hours)} half
                    </p>
                  </div>
                </div>
              )}

              {/* Roster date breakdown */}
              {rosterDates.length > 0 && (
                <>
                  <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                    <div className="col-span-3">Date</div>
                    <div className="col-span-3">Day</div>
                    <div className="col-span-3">Type</div>
                    <div className="col-span-2 text-center">Hours</div>
                    <div className="col-span-1 text-center">Status</div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {rosterDates.map((rd, idx) => {
                      const cfg = TYPE_CFG[rd.dayType] || TYPE_CFG['off day'];
                      const isPassed = rd.date <= new Date().toISOString().slice(0, 10);
                      const h = rd.dayType === 'off day' ? 8.5 : 4;
                      return (
                        <div key={idx} className={`grid grid-cols-12 px-5 py-2.5 border-b border-gray-100 last:border-0 items-center gap-x-2 ${isPassed ? '' : 'opacity-50'}`}>
                          <div className="col-span-3">
                            <p className="text-sm font-medium text-gray-800">
                              {new Date(rd.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <div className="col-span-3">
                            <p className="text-xs text-gray-500">
                              {new Date(rd.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' })}
                            </p>
                          </div>
                          <div className="col-span-3">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${cfg.badge}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="col-span-2 text-center">
                            <span className="text-xs font-bold text-gray-700">{fmtH(h)}</span>
                          </div>
                          <div className="col-span-1 text-center">
                            {isPassed
                              ? <span className="text-[10px] font-semibold text-emerald-600">Passed</span>
                              : <span className="text-[10px] font-semibold text-gray-400">Upcoming</span>
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Specific leaves from staff_unavailability */}
              {specificLeaves.length > 0 && (
                <>
                  <div className="px-5 py-2.5 border-t border-dashed border-gray-200 bg-gray-50/50">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Specific Leaves / Unavailability ({specificLeaves.length})
                    </p>
                  </div>
                  {specificLeaves.map((lv, idx) => (
                    <div key={idx} className="grid grid-cols-12 px-5 py-2.5 border-b border-gray-100 last:border-0 items-center gap-x-2">
                      <div className="col-span-3">
                        <p className="text-sm font-medium text-gray-800">
                          {new Date(lv.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${lv.type === 'leave' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {lv.type}
                        </span>
                      </div>
                      <div className="col-span-4 min-w-0">
                        <p className="text-xs text-gray-600 truncate">{lv.reason || '—'}</p>
                      </div>
                      <div className="col-span-3 text-right text-xs text-gray-400">
                        {lv.startTime && lv.endTime ? `${lv.startTime} – ${lv.endTime}` : 'Full day'}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })()}

        {/* Leave & Unavailability Records */}
        {staffData?.dayOffSummary && (() => {
          const records = staffData.dayOffSummary.specificLeaves || [];

          const fmt12h = (t) => {
            if (!t) return '—';
            const [h, m] = t.split(':').map(Number);
            const suffix = h >= 12 ? 'PM' : 'AM';
            const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
            return `${h12}:${String(m || 0).padStart(2, '0')} ${suffix}`;
          };

          const TIME_PERIODS = {
            'leave':                  { start: '08:30', end: '17:00' },
            'morning half day leave': { start: '08:30', end: '12:30' },
            'evening half day leave': { start: '13:00', end: '17:00' },
          };

          const TYPE_META = {
            'leave':                  { label: 'Full Day Leave',        badge: 'bg-amber-100 text-amber-700 border-amber-200' },
            'morning half day leave': { label: 'Morning Half Day Leave', badge: 'bg-orange-100 text-orange-700 border-orange-200' },
            'evening half day leave': { label: 'Evening Half Day Leave', badge: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
            'unavailability':         { label: 'Unavailability',         badge: 'bg-rose-100 text-rose-700 border-rose-200' },
          };

          const getTimePeriod = (rec) => {
            if (rec.type === 'unavailability') {
              return `${fmt12h(rec.startTime)} – ${fmt12h(rec.endTime)}`;
            }
            const p = TIME_PERIODS[rec.type];
            return p ? `${fmt12h(p.start)} – ${fmt12h(p.end)}` : '—';
          };

          const toMinutes = (t) => {
            if (!t) return 0;
            const [h, m] = t.split(':').map(Number);
            return h * 60 + (m || 0);
          };

          const getHours = (rec) => {
            const p = TIME_PERIODS[rec.type];
            const start = rec.type === 'unavailability' ? rec.startTime : p?.start;
            const end   = rec.type === 'unavailability' ? rec.endTime   : p?.end;
            if (!start || !end) return 0;
            return Math.max(0, (toMinutes(end) - toMinutes(start)) / 60);
          };

          const fmtHrs = (h) => {
            const hrs  = Math.floor(h);
            const mins = Math.round((h - hrs) * 60);
            return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
          };

          const totalHours = records.reduce((sum, r) => sum + getHours(r), 0);

          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                <div className="w-7 h-7 bg-violet-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Leave &amp; Unavailability Records</p>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${records.length > 0 ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                  {records.length}
                </span>
                {records.length > 0 && (
                  <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    {fmtHrs(totalHours)} total
                  </span>
                )}
              </div>

              {records.length === 0 && (
                <div className="flex items-center gap-2 px-5 py-5 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                  <p className="text-sm">No leave or unavailability records found within this date range.</p>
                </div>
              )}

              {records.length > 0 && (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-1">Day</div>
                    <div className="col-span-3">Type</div>
                    <div className="col-span-3">Time Period</div>
                    <div className="col-span-1 text-center">Hours</div>
                    <div className="col-span-2">Reason</div>
                  </div>

                  {records.map((rec, idx) => {
                    const meta = TYPE_META[rec.type] || { label: rec.type, badge: 'bg-gray-100 text-gray-600 border-gray-200' };
                    const timePeriod = getTimePeriod(rec);
                    const dateObj = rec.date ? new Date(rec.date + 'T00:00:00') : null;
                    return (
                      <div key={idx} className="grid grid-cols-12 px-5 py-3 border-b border-gray-100 last:border-0 hover:bg-violet-50/20 items-center gap-x-2">
                        {/* Date */}
                        <div className="col-span-2">
                          <p className="text-sm font-medium text-gray-800">
                            {dateObj ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </p>
                        </div>
                        {/* Day */}
                        <div className="col-span-1">
                          <p className="text-xs text-gray-500">
                            {dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'short' }) : '—'}
                          </p>
                        </div>
                        {/* Type */}
                        <div className="col-span-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${meta.badge}`}>
                            {meta.label}
                          </span>
                        </div>
                        {/* Time period */}
                        <div className="col-span-3">
                          <p className="text-xs font-mono font-medium text-gray-700">{timePeriod}</p>
                        </div>
                        {/* Hours */}
                        <div className="col-span-1 text-center">
                          <span className="text-xs font-bold text-red-600">{fmtHrs(getHours(rec))}</span>
                        </div>
                        {/* Reason */}
                        <div className="col-span-2 min-w-0">
                          <p className="text-xs text-gray-600 truncate" title={rec.reason}>{rec.reason || '—'}</p>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })()}

        {/* Working Hours Analysis Summary */}
        {staffData && report && (() => {
          const fmtHrs = (h) => {
            const hrs  = Math.floor(Math.abs(h));
            const mins = Math.round((Math.abs(h) - hrs) * 60);
            return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
          };
          const toMins = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };

          // Total calendar days up to today (future dates excluded)
          const effectiveEnd = report.effectiveEndDate || report.endDate;
          const s = new Date(report.startDate + 'T00:00:00');
          const e = new Date(effectiveEnd      + 'T00:00:00');
          const totalDays  = Math.max(0, Math.round((e - s) / 86400000) + 1);
          const totalAvail = Math.round(totalDays * 8.5 * 100) / 100;

          // Deduction 1: roster-based absent hours (off days + half days)
          const rosterAbsent = staffData.dayOffSummary?.totalAbsentHours || 0;

          // Deduction 2: specific leave & unavailability record hours
          const FIXED_PERIODS = {
            'leave':                  { start: '08:30', end: '17:00' },
            'morning half day leave': { start: '08:30', end: '12:30' },
            'evening half day leave': { start: '13:00', end: '17:00' },
          };
          const leaveRecordHours = (staffData.dayOffSummary?.specificLeaves || []).reduce((sum, r) => {
            const p = FIXED_PERIODS[r.type];
            const st = r.type === 'unavailability' ? r.startTime : p?.start;
            const en = r.type === 'unavailability' ? r.endTime   : p?.end;
            if (!st || !en) return sum;
            return sum + Math.max(0, (toMins(en) - toMins(st)) / 60);
          }, 0);

          // Deduction 3: teaching hours
          const teachingHours = staffData.lecturerHours?.totalHours || 0;

          // Deduction 4: event / sub-event hours (other_events assigned to this staff member)
          const eventHours = staffData.totalOtherHours || 0;

          const totalDeducted = rosterAbsent + leaveRecordHours + teachingHours + eventHours;
          const freeHours     = Math.round((totalAvail - totalDeducted) * 100) / 100;
          const freeColor     = freeHours >= 0 ? 'text-emerald-700' : 'text-red-600';
          const freeBg        = freeHours >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';

          const rows = [
            { label: 'Total Available Hours',     value: `${totalDays} days × 8.5h`,         hours: totalAvail,      color: 'text-gray-800', sign: '' },
            { label: 'Roster Absent Hours',        value: 'Off days + half days (schedule)',   hours: rosterAbsent,    color: 'text-red-500',  sign: '−' },
            { label: 'Leave & Unavailability',     value: 'Submitted leave records',           hours: leaveRecordHours,color: 'text-red-500',  sign: '−' },
            { label: 'Lecturer Teaching Hours',    value: 'Unique class sessions in range',    hours: teachingHours,   color: 'text-red-500',  sign: '−' },
            { label: 'Event / Sub-Event Hours',    value: 'Events assigned in range',          hours: eventHours,      color: 'text-red-500',  sign: '−' },
          ];

          return (
            <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Working Hours Analysis</p>
                <p className="text-[11px] text-gray-400 ml-1">
                  {new Date(report.startDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {' → '}
                  {new Date(effectiveEnd + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {' · '}{totalDays} days
                  {report.effectiveEndDate && report.effectiveEndDate < report.endDate && (
                    <span className="ml-1 text-amber-500">(capped at today)</span>
                  )}
                </p>
              </div>

              {/* Calculation rows */}
              <div className="divide-y divide-gray-100">
                {rows.map((row, idx) => (
                  <div key={idx} className="flex items-center justify-between px-6 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{row.label}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{row.value}</p>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <span className={`text-sm font-bold ${row.color}`}>
                        {row.sign} {fmtHrs(row.hours)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider + result */}
              <div className={`mx-5 mb-5 mt-1 rounded-xl border px-6 py-4 ${freeBg} flex items-center justify-between`}>
                <div>
                  <p className={`text-base font-bold ${freeColor}`}>Estimated Free Hours</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Available − Absent − Leaves − Teaching − Events
                  </p>
                </div>
                <div className={`text-2xl font-black ${freeColor}`}>
                  {freeHours < 0 ? '−' : ''}{fmtHrs(Math.abs(freeHours))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Empty state */}
        {!report && !generating && !genError && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
            </svg>
            <p className="text-sm font-medium text-gray-500">Select a staff member and date range, then click Generate</p>
          </div>
        )}
      </div>

      {modalSubject && <StudentDetailModal subject={modalSubject} onClose={closeModal} />}
      {selectedSubjectDetail && (
        <AssignmentDetailModal data={selectedSubjectDetail} onClose={() => setSelectedSubjectDetail(null)} />
      )}
    </DashboardLayout>
  );
}
